"use client";

import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import { MacroLineFigure } from "@/components/tools/econ-macro/line-figure";
import type { Strings } from "@/components/tools/multiplier-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type MoneyResult,
  roundTable,
} from "@/lib/tool-kit/econ-macro/multipliers";

const ROUNDS = 10;

export function MoneyResults({
  result,
  reserve,
  excess,
  cash,
  newBase,
  strings,
}: {
  result: MoneyResult;
  reserve: number;
  excess: number;
  cash: number;
  newBase: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const n = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  const amount = (value: number) => fmt(value, { maximumFractionDigits: 2 });
  const leakage = reserve + excess + cash;
  const values = {
    rr: n(reserve),
    e: n(excess),
    c: n(cash),
    leak: n(leakage),
    dm: n(result.depositMultiplier),
    mm: n(result.moneyMultiplier),
    base: amount(newBase),
    deposits: amount(result.depositsCreated),
    lending: amount(result.newLending),
  };
  const showRounds = cash === 0 && newBase > 0;
  const rows = showRounds
    ? roundTable(newBase, 1 - reserve - excess, ROUNDS)
    : [];
  const last = rows.at(-1);
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.depositMultiplier}
          value={n(result.depositMultiplier)}
        />
        <StatCard
          label={strings.moneyMultiplierCard}
          value={n(result.moneyMultiplier)}
        />
        <StatCard
          label={strings.simpleMultiplier}
          value={Number.isNaN(result.simple) ? "—" : n(result.simple)}
        />
        <StatCard
          label={strings.depositsCreated}
          value={amount(result.depositsCreated)}
        />
        <StatCard
          label={strings.moneySupplyChange}
          value={amount(result.moneySupplyChange)}
        />
        <StatCard
          label={strings.newLending}
          value={amount(result.newLending)}
        />
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className={line}>{t(strings.workDeposit, values)}</p>
        <p className={line}>{t(strings.workMoney, values)}</p>
        <p className={line}>{t(strings.workDeposits, values)}</p>
        <p className={line}>{t(strings.workLending, values)}</p>
      </div>
      {cash > 0 ? <Notice>{strings.cashNote}</Notice> : null}
      {last && showRounds ? (
        <>
          <h3 className={WIDGET_H3}>{strings.depositRoundsHeading}</h3>
          <p className="text-muted-foreground text-sm">
            {t(strings.depositRoundsIntro, {
              rounds: fmt(ROUNDS),
              share: fmt(last.share * 100, { maximumFractionDigits: 1 }),
            })}
          </p>
          <DataTable
            head={[
              strings.roundColumn,
              strings.newDepositColumn,
              strings.reservesColumn,
              strings.loansColumn,
              strings.totalDepositsColumn,
            ]}
            rows={rows.map((row) => [
              fmt(row.round),
              amount(row.amount),
              amount(row.amount * (reserve + excess)),
              amount(row.amount * (1 - reserve - excess)),
              amount(row.cumulative),
            ])}
          />
          <MacroLineFigure
            description={t(strings.chartDescriptionMoney, {
              rounds: fmt(ROUNDS),
              total: amount(result.depositsCreated),
            })}
            fallbackName="deposit-expansion"
            fmt={fmt}
            id="mc-money-chart"
            labels={strings}
            names={[strings.seriesDeposits, strings.seriesDepositLimit]}
            numeric
            rows={rows.map((row) => ({
              label: fmt(row.round),
              x: row.round,
              values: [row.cumulative, result.depositsCreated],
            }))}
            title={strings.chartTitleMoney}
            xTitle={strings.chartXMoney}
            yTitle={strings.chartYMoney}
          />
        </>
      ) : null}
      <Notice>{strings.assumptionsMoney}</Notice>
    </div>
  );
}
